import type { NextRouter } from 'next/router'
import type { NavGroup, NavLink, VerticalNavItemsType } from 'src/@core/layouts/types'
import { resolveNavPath } from 'src/@core/layouts/utils'
import { isNavGroup, isNavSectionTitle, type DrillFrame } from './navGuards'
import { isPrimaryNavLinkActive } from './navLinkActive'

/**
 * Builds the rail drill stack so the sidebar matches the current URL (e.g. Menu → Restaurant → …).
 * @param fullNavItems — Full tree (including Home) for route matching.
 * @param menuRootItems — First-frame items (typically Home stripped) shown in the scrollable menu.
 */
export function computeDrillStackForRoute(
  fullNavItems: VerticalNavItemsType,
  menuRootItems: VerticalNavItemsType,
  router: NextRouter,
  id: string | string[] | undefined
): DrillFrame[] {
  const idNorm = Array.isArray(id) ? id[0] : id

  function findAncestorChain(items: VerticalNavItemsType, ancestorGroups: NavGroup[]): NavGroup[] | 'top' | null {
    for (const item of items) {
      if (isNavSectionTitle(item)) continue

      if (!isNavGroup(item)) {
        const link = item as NavLink
        const href = resolveNavPath(link.path, idNorm)
        if (href && isPrimaryNavLinkActive(router, href)) {
          return ancestorGroups.length === 0 ? 'top' : ancestorGroups
        }
      } else {
        const g = item as NavGroup
        const ch = (g.children ?? []) as VerticalNavItemsType
        const res = findAncestorChain(ch, [...ancestorGroups, g])
        if (res !== null) return res
      }
    }

    return null
  }

  const base: DrillFrame[] = [{ title: 'Menu', items: menuRootItems }]
  const chain = findAncestorChain(fullNavItems, [])

  if (chain === null || chain === 'top') {
    return base
  }

  for (const g of chain) {
    base.push({ title: g.title, items: (g.children ?? []) as VerticalNavItemsType })
  }

  return base
}
